import {
  Box,
  Button,
  Container,
  Field,
  Heading,
  IconButton,
  Input,
  Stack,
  Text,
} from "@chakra-ui/react";
import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useState } from "react";
import { IoIosCheckmarkCircle } from "react-icons/io";
import { IoAddCircle, IoClose } from "react-icons/io5";
import { v4 as uuidv4 } from "uuid";
import { useFixedCosts } from "../context/FixedCostsContext";
import { useTransactions } from "../context/TransactionsContext";
import { useCurrentMonth } from "../hooks/useCurrentMonth";
import {
  formatEuro,
  formatMonthYear,
  getTransactionListDate,
} from "../utils/helperFunctions";
import CustomSelect from "./CustomSelect";
import { Toaster, toaster } from "./ui/toaster";
import { useCustomCategories } from "../context/CustomCatgoriesContext";

type Entry = {
  description: string;
  category: string | undefined;
  amount: string;
};

export const FixedCosts = () => {
  const { fixedCosts, addFixedCost, updateFixedCost } = useFixedCosts();
  const currentMonth = useCurrentMonth();
  const { addTransaction } = useTransactions();
  const MotionText = motion(Text);
  const { categories: customCategories } = useCustomCategories();
  const [hasAnimated, setHasAnimated] = useState(false);
  const [entries, setEntries] = useState<Entry[]>([
    { category: "", amount: "", description: "" },
  ]);

  useEffect(() => {
    if (fixedCosts.length === 0) {
      setEntries([{ category: "", amount: "", description: "" }]);
    } else {
      setEntries(
        fixedCosts.map((fc) => ({
          category: fc.category || "",
          amount: fc.amount.toString(),
          description: fc.description || "",
        }))
      );
    }
  }, [fixedCosts]);

  useEffect(() => {
    setHasAnimated(true);
  }, []);

  const handleAddEntry = () => {
    setEntries([
      ...entries,
      { category: undefined, amount: "", description: "" },
    ]);
  };

  const handleChange = (index: number, field: keyof Entry, value: string) => {
    setEntries((prev) => {
      const updated = [...prev];
      updated[index][field] = value;
      return updated;
    });
  };

  const handleAddToMonth = () => {
    entries.forEach((entry, index) => {
      if (entry.category && entry.amount) {
        const parsedAmount = parseFloat(entry.amount);
        if (fixedCosts[index]) {
          updateFixedCost(
            index,
            entry.category,
            parsedAmount,
            entry.description
          );
        } else {
          addFixedCost(entry.category, parsedAmount, entry.description);
        }

        const now = new Date();
        addTransaction({
          id: uuidv4(),
          type: "expense",
          category: entry.category,
          description: entry.description || "Fixkosten",
          amount: parsedAmount,
          date: now.toISOString(),
          transactionListDate: getTransactionListDate(now),
        });
      }
    });
    toaster.create({
      description: "Die Fixkosten wurden erfolgreich zum Monat hinzugefügt",
      type: "success",
      placement: "top-end",
    });
  };

  const handleRemoveEntry = (index: number) => {
    setEntries((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <Container maxW="lg" pt={4}>
      <Heading pb={6} textAlign="center">
        Fixkosten – {formatMonthYear(currentMonth)}
      </Heading>
      <Box mt={4} p={4} bg="gray.100" borderRadius="md" textAlign="center">
        <Text color="gray.700">Summe Fixkosten:</Text>
        {entries.length === 0 ? (
          <Text fontSize="2xl" fontWeight={"bolder"} color={"red"}>
            0,00€
          </Text>
        ) : (
          <Text fontSize="2xl" fontWeight={"bolder"} color={"red"}>
            {formatEuro(
              entries.reduce((acc, entry) => {
                const parsed = parseFloat(entry.amount);
                return acc + (isNaN(parsed) ? 0 : parsed);
              }, 0)
            )}
          </Text>
        )}
      </Box>
      <Button mt={6} width="full" onClick={handleAddToMonth}>
        Fixkosten zum Monat hinzufügen
        {/* @ts-ignore */}
        <IoIosCheckmarkCircle />
      </Button>
      {entries.map((entry, index) => {
        return (
          <>
            <Toaster />
            <Box
              mt={10}
              p={6}
              borderWidth="1px"
              borderRadius="md"
              boxShadow="md"
              key={index}
              mb={6}
              position="relative"
            >
              <IconButton
                aria-label="Eintrag löschen"
                size="sm"
                colorScheme="red"
                position="absolute"
                top={2}
                right={2}
                variant="ghost"
                onClick={() => handleRemoveEntry(index)}
              >
                {/* @ts-ignore */}
                {<IoClose />}
              </IconButton>

              <Stack spaceY={4}>
                <Field.Root>
                  <Field.Label>Beschreibung</Field.Label>
                  <Input
                    borderColor="gray"
                    placeholder="z. B. Spotify Abo"
                    value={entry.description}
                    onChange={(e) =>
                      handleChange(index, "description", e.target.value)
                    }
                  />
                </Field.Root>

                <Field.Root>
                  <Field.Label>Kategorie</Field.Label>
                  <CustomSelect
                    options={customCategories}
                    value={entry.category}
                    placeholder="Kategorie auswählen"
                    onChange={(val: string) =>
                      handleChange(index, "category", val)
                    }
                  />
                </Field.Root>

                <Field.Root>
                  <Field.Label>Betrag</Field.Label>
                  <Input
                    type="number"
                    borderColor="gray"
                    placeholder="€ Betrag"
                    value={entry.amount}
                    onChange={(e) =>
                      handleChange(index, "amount", e.target.value)
                    }
                  />
                </Field.Root>
              </Stack>
            </Box>
          </>
        );
      })}

      <Box display="flex" flexDirection="column" alignItems="center">
        <IconButton
          aria-label="Eintrag hinzufügen"
          onClick={handleAddEntry}
          width={10}
          mt={4}
          bg="white"
          borderRadius="full"
          height={10}
          _hover={{ bg: "gray.200" }}
        >
          {
            //@ts-ignore
            <IoAddCircle color="black" style={{ width: "auto", height: 50 }} />
          }
        </IconButton>
        <AnimatePresence>
          {hasAnimated && (
            <MotionText
              mt={2}
              mb={12}
              fontSize="sm"
              color="gray.600"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              transition={{ duration: 0.6 }}
            >
              Neue Fixkosten eintragen
            </MotionText>
          )}
        </AnimatePresence>
      </Box>
    </Container>
  );
};
